import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { api } from "@/lib/api";
import type { NavButton } from "@/components/MunicipalityForm";

export type { NavButton };

export interface TotemConfig {
  city: string;
  lat: number;
  lon: number;
  municipality: string;
  municipalityId: string;
  apiKey?: string;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  proxyBlockedDomains: string[];
  screensaverEnabled: boolean;
  isLoading: boolean;
}

interface MunicipalityAssets {
  id?: string;
  latitude?: number;
  longitude?: number;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  proxyBlockedDomains: string[];
  screensaverEnabled: boolean;
}

interface HeroDataDB {
  name: string;
  image_url: string;
  click_url: string;
}

// Fallback configuration map (used when DB is not available)
const FALLBACK_MUNICIPALITY_CONFIGS: Record<string, MunicipalityAssets> = {
  "Los Alcázares": {
    id: "00000000-0000-4000-8000-000000000074",
    latitude: 37.7481843,
    longitude: -0.8480655,
    heroes: [{
      name: "Los Alcázares",
      imageUrl: "https://turismo.losalcazares.es/wp-content/uploads/2026/03/slider11slider-6.jpg",
      clickUrl: "https://turismoalcazares.ciceroneweb.com/home"
    }],
    navButtons: [
      { id: "agenda", label: "Agenda", icon: "Calendar", action: "internal_page", page: "/agenda" },
      { id: "playas", label: "Nuestras playas", icon: "Sunset", action: "internal_page", page: "/descubre/playas" },
      { id: "patrimonio", label: "Patrimonio", icon: "Building", action: "internal_page", page: "/descubre/patrimonio" },
      { id: "cultura", label: "Cultura", icon: "Star", action: "internal_page", page: "/descubre/cultura" },
    ],
    bottomButtons: [
      { id: "informacion", label: "Información y servicios", icon: "Info", action: "internal_page", page: "/descubre/informacion" },
      { id: "folletos", label: "Folletos", icon: "Route", action: "internal_page", page: "/folletos" },
    ],
    newsletterUrl: "https://turismo.losalcazares.es/informacion-y-servicios/",
    carpetaniaUrl: "https://turismo.losalcazares.es/rutas-turisticas/",
    enjoyUrl: "https://turismo.losalcazares.es/",
    proxyBlockedDomains: [],
    screensaverEnabled: true,
  }
};

const URLConfigContext = createContext<TotemConfig | null>(null);

export const URLConfigProvider = ({ children }: { children: ReactNode }) => {
  const [municipalitiesFromDB, setMunicipalitiesFromDB] = useState<Record<string, MunicipalityAssets>>({});
  const [dbLoaded, setDbLoaded] = useState(false);
  
  // Almacenar los params de URL iniciales una sola vez al montar
  // Esto previene que navigate('/') cause pérdida de configuración
  const [initialParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      city: params.get('city') || params.get('municipality') || "Los Alcázares",
      lat: params.has('lat') ? parseFloat(params.get('lat') || '') : undefined,
      lon: params.has('lon') ? parseFloat(params.get('lon') || '') : undefined,
      apiKey: params.get('apiKey') || ''
    };
  });

  useEffect(() => {
    const loadMunicipalities = async () => {
      try {
        const data = await api.municipalities.list();

        if (data && data.length > 0) {
          const municipalitiesMap: Record<string, MunicipalityAssets> = {};
          data.forEach((municipality: any) => {
            const heroesData = municipality.heroes_data as HeroDataDB[];
            const heroes = Array.isArray(heroesData) && heroesData.length > 0
              ? heroesData.map(hero => ({
                  name: hero.name,
                  imageUrl: hero.image_url || FALLBACK_MUNICIPALITY_CONFIGS['Los Alcázares'].heroes[0].imageUrl,
                  clickUrl: hero.click_url
                }))
              : [{
                  name: municipality.name,
                  imageUrl: FALLBACK_MUNICIPALITY_CONFIGS['Los Alcázares'].heroes[0].imageUrl,
                  clickUrl: municipality.municipality_web_url || 'https://turismoalcazares.ciceroneweb.com/home'
                }];

    municipalitiesMap[municipality.name] = {
              id: municipality.id,
              latitude: Number(municipality.latitude),
              longitude: Number(municipality.longitude),
              heroes,
              navButtons: (municipality.nav_buttons as NavButton[]) ?? [],
              bottomButtons: (municipality.bottom_buttons as NavButton[]) ?? [],
              newsletterUrl: municipality.newsletter_url,
              carpetaniaUrl: municipality.carpetania_url,
              enjoyUrl: municipality.enjoy_url,
              proxyBlockedDomains: municipality.proxy_blocked_domains ?? [],
              screensaverEnabled: municipality.screensaver_enabled ?? true,
            };
          });
          setMunicipalitiesFromDB(municipalitiesMap);
        }
      } catch (error) {
        console.error('Error in loadMunicipalities:', error);
      } finally {
        setDbLoaded(true);
      }
    };

    loadMunicipalities();

    // Reload cuando la pestaña recupera el foco (sin realtime de Supabase)
    const handleFocus = () => loadMunicipalities();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const config = useMemo<TotemConfig>(() => {
    // Usar los valores almacenados en estado en lugar de window.location.search
    // para evitar pérdida de configuración cuando navigate('/') limpia los params
    const { city, lat: urlLat, lon: urlLon, apiKey } = initialParams;

    // Use DB data if loaded, otherwise fallback to hardcoded config
    const MUNICIPALITY_CONFIGS = {
      ...FALLBACK_MUNICIPALITY_CONFIGS,
      ...municipalitiesFromDB,
    };

    // Resolver con varios fallbacks para nunca devolver undefined:
    // 1) municipio solicitado, 2) Los Alcázares, 3) primer disponible
    const fallbackKey = Object.keys(MUNICIPALITY_CONFIGS)[0];
    const municipalityAssets =
      MUNICIPALITY_CONFIGS[city] ??
      MUNICIPALITY_CONFIGS['Los Alcázares'] ??
      (fallbackKey ? MUNICIPALITY_CONFIGS[fallbackKey] : undefined);

    if (!municipalityAssets) {
      // BD vacía y sin fallback: devolver una configuración mínima en lugar de crashear
      return {
        city,
        lat: Number.isFinite(urlLat) ? urlLat! : 37.7481843,
        lon: Number.isFinite(urlLon) ? urlLon! : -0.8480655,
        municipality: city,
        municipalityId: '',
        apiKey,
        heroes: [],
        navButtons: [],
        bottomButtons: [],
        newsletterUrl: '',
        carpetaniaUrl: '',
          enjoyUrl: '',
          proxyBlockedDomains: [],
          screensaverEnabled: true,
          isLoading: !dbLoaded,
      };
    }

    return {
      city,
      lat: Number.isFinite(urlLat) ? urlLat! : (municipalityAssets.latitude ?? 37.7481843),
      lon: Number.isFinite(urlLon) ? urlLon! : (municipalityAssets.longitude ?? -0.8480655),
      municipality: city,
      municipalityId: municipalityAssets.id || '',
      apiKey,
      heroes: municipalityAssets.heroes,
      navButtons: municipalityAssets.navButtons,
      bottomButtons: municipalityAssets.bottomButtons,
      newsletterUrl: municipalityAssets.newsletterUrl,
      carpetaniaUrl: municipalityAssets.carpetaniaUrl,
      enjoyUrl: municipalityAssets.enjoyUrl,
      proxyBlockedDomains: municipalityAssets.proxyBlockedDomains,
      screensaverEnabled: municipalityAssets.screensaverEnabled,
      isLoading: !dbLoaded
    };
  }, [municipalitiesFromDB, dbLoaded, initialParams]);

  return (
    <URLConfigContext.Provider value={config}>
      {children}
    </URLConfigContext.Provider>
  );
};

export const useURLConfigContext = (): TotemConfig => {
  const context = useContext(URLConfigContext);
  if (!context) {
    throw new Error('useURLConfigContext must be used within a URLConfigProvider');
  }
  return context;
};
